// Light / Dark / Auto, with light as the default.
//
// The CSS only ever sees an explicit data-theme of "light" or "dark". "Auto"
// is resolved here in JS rather than with a prefers-color-scheme media query,
// because the two mechanisms fight: a media query cannot be overridden by a
// user choice without duplicating every token behind a :not([data-theme])
// guard, and that is exactly where unreadable half-themed pages come from.
//
// The matching no-flash bootstrap lives inline in index.html. It must stay in
// sync with resolve() below.

const KEY = 'floatdesk.theme';
export const CHOICES = ['light', 'dark', 'auto'];

const media = () => window.matchMedia('(prefers-color-scheme: dark)');

export function getChoice() {
  try {
    const stored = localStorage.getItem(KEY);
    return CHOICES.includes(stored) ? stored : 'light';
  } catch {
    return 'light';
  }
}

export function resolve(choice = getChoice()) {
  if (choice === 'auto') return media().matches ? 'dark' : 'light';
  return choice;
}

function apply(choice) {
  const resolved = resolve(choice);
  document.documentElement.setAttribute('data-theme', resolved);
  // Tells the browser which scrollbar and form-control palette to paint, so
  // native chrome matches the page instead of staying stubbornly light.
  document.documentElement.style.colorScheme = resolved;
}

const listeners = new Set();

export function subscribeTheme(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setTheme(choice) {
  if (!CHOICES.includes(choice)) return;
  try { localStorage.setItem(KEY, choice); } catch { /* private window */ }
  apply(choice);
  listeners.forEach((fn) => fn(choice));
}

/** Call once at startup. Keeps Auto tracking the OS while it stays selected. */
export function initTheme() {
  apply(getChoice());
  const mq = media();
  const onChange = () => { if (getChoice() === 'auto') apply('auto'); };
  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else mq.addListener(onChange);
}
